import { hasPart, toBinaryString } from 'src/app/helpers/binaries/operations';
import { defaultUnitSIDC } from 'src/app/helpers/units.helper';
import { ETaraflarArasiIliskiKontrolü, ETaraflarArasiIliskiler } from 'src/enums/mixed.enum';
import {
  DenizAraciParametreleriResultType,
  DenizAraciParametreleriFormType,
} from 'src/types/services/hymotsc/deniz-araci-parametreleri.type';
import {
  HavaAraciParametreleriResultType,
  HavaAraciParametreleriFormType,
} from 'src/types/services/hymotsc/hava-araci-parametreleri.type';
import { InsanYapimiEngelTuruResultType } from 'src/types/services/hymotsc/insan-yapimi-engel-turu.type';
import { MuhimmatTurleriResultType, MuhimmatTurleriFormType } from 'src/types/services/hymotsc/muhimmat-turleri.type';
import {
  TmkKalemiOzellikleriResultType,
  TmkKalemiOzellikleriFormType,
} from 'src/types/services/hymotsc/tmk-kalemi-ozellikleri.type';

const muhimmatBalistikSearchStr = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) => {
  if (typeof muhimmat.balistikOzellikleri === 'number') {
    return toBinaryString(muhimmat.balistikOzellikleri, 2, 16);
  }
  return muhimmat.balistikOzellikleri;
};

const muhimmatSeyirSearchStr = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) => {
  if (typeof muhimmat.güdümSistemi === 'number') {
    return toBinaryString(muhimmat.güdümSistemi, 2, 24);
  }
  return muhimmat.güdümSistemi;
};

const tmkKategoriSearchStr = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) => {
  if (typeof system.kategorisi === 'number') {
    return toBinaryString(system.kategorisi, 2, 8);
  }
  return system.kategorisi;
};

const engelKategoriSearchStr = (engel: InsanYapimiEngelTuruResultType) => {
  if (typeof engel.kategori === 'number') {
    return toBinaryString(engel.kategori, 2, 16);
  }
  return engel.kategori;
};

export const denizAraciKategoriSearchStr = (
  denizAraci: DenizAraciParametreleriResultType | DenizAraciParametreleriFormType,
) => {
  if (typeof denizAraci.kategorisi === 'number') {
    return toBinaryString(denizAraci.kategorisi, 2, 24);
  }
  return denizAraci.kategorisi;
};

const tmkKarakteristikSearchStr = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) => {
  if (typeof system.silahZirhAmfibiSensorOzelligi === 'number') {
    return toBinaryString(system.silahZirhAmfibiSensorOzelligi, 2, 8);
  }
  return system.silahZirhAmfibiSensorOzelligi;
};

export const havaAraciKategoriSearchStr = (
  havaAraci: HavaAraciParametreleriResultType | HavaAraciParametreleriFormType,
) => {
  if (typeof havaAraci.kategorisi === 'number') {
    return toBinaryString(havaAraci.kategorisi, 2, 16);
  }
  return havaAraci.kategorisi;
};

export const ikmalTuketimTuruSearchStr = (tuketimTuru: string | number) => {
  if (typeof tuketimTuru === 'number') {
    return toBinaryString(tuketimTuru, 2, 8);
  }
  return tuketimTuru;
};

export const isLandSymbol = (sidc?: string) => hasPart(sidc || defaultUnitSIDC, ['10', '11'], 5);

export const denizAraciIsDenizalti = (navalcraft: DenizAraciParametreleriResultType) =>
  hasPart(denizAraciKategoriSearchStr(navalcraft), '1', 1);

export const tmkIsLiving = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) =>
  hasPart(tmkKategoriSearchStr(system), '00', 1);

export const tmkIsVehicle = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) =>
  hasPart(tmkKategoriSearchStr(system), '01', 1);

export const tmkIsWeapon = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) =>
  hasPart(tmkKategoriSearchStr(system), '10', 1);

export const tmkIsSensor = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) =>
  hasPart(tmkKategoriSearchStr(system), '11', 1);

export const tmkIsNotSensor = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) =>
  hasPart(tmkKategoriSearchStr(system), ['00', '01', '10'], 1);

export const tmkAlgiYapabilirMi = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) => {
  return tmkIsNotSensor(system) || (tmkIsSensor(system) && tmkKarakteristikUnnamed1(system));
};

export const tmkVehicleHasArmor = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) =>
  tmkIsVehicle(system) && hasPart(tmkKategoriSearchStr(system), ['10', '11'], 5);

export const tmkKategoriIsMountableOnVehicle = (
  system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType,
) => {
  return tmkIsWeapon(system) && hasPart(tmkKategoriSearchStr(system), '01', 3);
};

export const tmkHasCharacteristicFeatures = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) => {
  return tmkVehicleHasArmor(system) || tmkIsWeapon(system) || tmkIsSensor(system);
};

/** Özellikler 0xxxxxxx olması halinde. */
export const tmkKarakteristikUnnamed1 = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) =>
  hasPart(tmkKarakteristikSearchStr(system), '0', 1);

/** Özellikler 10xxxxxx ise */
export const tmkKarakteristikUnnamed2 = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) =>
  hasPart(tmkKarakteristikSearchStr(system), '10', 1);

/** Özellikler 1111xxxx ise */
export const tmkKarakteristikUnnamed3 = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) =>
  hasPart(tmkKarakteristikSearchStr(system), '1111', 1);

/** Özellikler 11xxxxxx ise */
export const tmkKarakteristikUnnamed4 = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) =>
  hasPart(tmkKarakteristikSearchStr(system), '0', 1);

/* ABOUT INFRASTRUCTURE */
export const tmkKarakteristikIsRoadConstruction = (
  system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType,
) => hasPart(tmkKarakteristikSearchStr(system), '0010', 5) && hasPart(tmkKarakteristikSearchStr(system), '1111', 1);

export const tmkKarakteristikIsBridgingEquipmentorAssaultBridge = (
  system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType,
) =>
  hasPart(tmkKarakteristikSearchStr(system), ['0000', '0001'], 5) &&
  hasPart(tmkKarakteristikSearchStr(system), '1111', 1);

export const tmkKarakteristikIsTunnelConstruction = (
  system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType,
) => hasPart(tmkKarakteristikSearchStr(system), '0100', 5) && hasPart(tmkKarakteristikSearchStr(system), '1111', 1);

export const tmkKarakteristikIsRunwayConstruction = (
  system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType,
) => hasPart(tmkKarakteristikSearchStr(system), '0011', 5) && hasPart(tmkKarakteristikSearchStr(system), '1111', 1);

export const tmkKarakteristikIsBunkerConstruction = (
  system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType,
) => hasPart(tmkKarakteristikSearchStr(system), '0101', 5) && hasPart(tmkKarakteristikSearchStr(system), '1111', 1);

export const tmkIsJammer = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) =>
  hasPart(tmkKarakteristikSearchStr(system), '1000', 1) || hasPart(tmkKarakteristikSearchStr(system), '1001', 1);

export const muhimmatIsIlluminating = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), '1', 2);

export const muhimmatIsBlinding = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), '1', 3);

export const muhimmatIsCBRN = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), '1', 4);

export const muhimmatIsSeyirIzlenebilir = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), '1', 5);

export const muhimmatFixedTargetlerdeEtkili = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatSeyirSearchStr(muhimmat), '1', [1, 2, 3, 4, 5, 6, 7]) &&
  !hasPart(muhimmatSeyirSearchStr(muhimmat), '1', [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24]);

export const muhimmatIsSuAltiHedeflerineEtkili = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), '1', 7);

export const muhimmatIsHavaHedeflerineEtkili = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), '1', 10);

export const muhimmatIsDenizUstuHedeflerineEtkili = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), '1', 11);

export const muhimmatIsKaraHedeflerineEtkili = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), '1', 12);

export const muhimmatIsDirect = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), ['1'], 14);

export const muhimmatIsIndirect = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), ['0'], 14);

export const muhimmatIsNoktaEtkili = (muhimmat?: MuhimmatTurleriResultType | MuhimmatTurleriFormType) => {
  if (muhimmat) {
    return hasPart(muhimmatBalistikSearchStr(muhimmat), '1', 15);
  }
};

export const muhimmatIsAlanEtkili = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), '1', 16);

export const muhimmatHasRadiusEffects = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), '1', [1, 4, 16]);

export const muhimmatSudaYuzebilir = (muhimmat: MuhimmatTurleriResultType | MuhimmatTurleriFormType) =>
  hasPart(muhimmatBalistikSearchStr(muhimmat), '1', 6);

export const engelIsDenizMayini = (engel: InsanYapimiEngelTuruResultType) =>
  hasPart(engelKategoriSearchStr(engel), '01', [1]) && hasPart(engelKategoriSearchStr(engel), '10', [11]);

export const denizAraciIsAmfibi = (denizAraci: DenizAraciParametreleriResultType | DenizAraciParametreleriFormType) =>
  hasPart(denizAraciKategoriSearchStr(denizAraci), '10101', [12]);

export const denizAraciMayinHarbi = (denizAraci: DenizAraciParametreleriResultType | DenizAraciParametreleriFormType) =>
  hasPart(denizAraciKategoriSearchStr(denizAraci), '11101', [12]);

export const havaAraciIsRunwayOnly = (havaAraci: HavaAraciParametreleriResultType | HavaAraciParametreleriFormType) =>
  hasPart(havaAraciKategoriSearchStr(havaAraci), ['00'], [9]);

export const havaAraciKisaMesafeKalkisInisOnly = (
  havaAraci?: HavaAraciParametreleriResultType | HavaAraciParametreleriFormType,
) => havaAraci && hasPart(havaAraciKategoriSearchStr(havaAraci), ['10'], 9);

export const locationIsAtAnyPort = (ports: L.Polygon<any>[], location: L.LatLng) => {
  return ports.some((port) => port.getBounds().contains(location));
};

export const isAgentInsidePort = (ports: L.Polygon<any>[], unitLocation: L.LatLng) => {
  return ports.some((port) => port.getBounds().contains(unitLocation));
};

export const isTuketimSinirsiz = (tuketimTuru: string | number) =>
  hasPart(ikmalTuketimTuruSearchStr(tuketimTuru), ['0000', '0100'], [1]);

export const isWeaponEngageableWithOrder = (system: TmkKalemiOzellikleriResultType | TmkKalemiOzellikleriFormType) => {
  return hasPart(tmkKategoriSearchStr(system), ['10', '11'], [7]) && hasPart(tmkKategoriSearchStr(system), ['10'], [1]);
};

/**
 * An utility function to check whether given relation value satisfies the requested conditions.
 *
 * @param relation Queried side relation value
 * @param kontrolTürü Query type
 * @param defaultValue Default value to be returned incase of queried relation is missing
 * @returns
 */
export const sideRelationControl = (
  relation: number | undefined,
  kontrolTürü: ETaraflarArasiIliskiKontrolü,
  defaultValue = false,
): boolean => {
  if (relation === undefined) return defaultValue;

  switch (kontrolTürü) {
    case ETaraflarArasiIliskiKontrolü.KENDI_TARAFI:
      return relation === ETaraflarArasiIliskiler.SAME;

    case ETaraflarArasiIliskiKontrolü.DOST:
      return [ETaraflarArasiIliskiler.SAME, ETaraflarArasiIliskiler.ALLY, ETaraflarArasiIliskiler.FRIENDLY].includes(
        relation,
      );

    case ETaraflarArasiIliskiKontrolü.DOST_VE_TARAFSIZ:
      return [
        ETaraflarArasiIliskiler.SAME,
        ETaraflarArasiIliskiler.ALLY,
        ETaraflarArasiIliskiler.FRIENDLY,
        ETaraflarArasiIliskiler.NEUTRAL,
      ].includes(relation);

    case ETaraflarArasiIliskiKontrolü.DUSMAN:
      return [ETaraflarArasiIliskiler.SUSPICIOUS, ETaraflarArasiIliskiler.ENEMY].includes(relation);

    case ETaraflarArasiIliskiKontrolü.DUSMAN_VE_TARAFSIZ:
      return [
        ETaraflarArasiIliskiler.SUSPICIOUS,
        ETaraflarArasiIliskiler.ENEMY,
        ETaraflarArasiIliskiler.NEUTRAL,
      ].includes(relation);

    case ETaraflarArasiIliskiKontrolü.TARAFSIZ:
      return relation === ETaraflarArasiIliskiler.NEUTRAL;

    default:
      return defaultValue;
  }
};
